import React from "react";
import {
  SimpleForm,
  EditProps,
  useEditController,
  FormDataConsumer,
  ReferenceInput,
  required,
  AutocompleteInput,
  TextInput,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, Typography } from "@material-ui/core";
import CustomEditToolbar from "components/CustomEditToolbar";
import EditWrapper from "../../components/EditWrapper";
import CustomDatePickerInput from "../../components/CustomDatePickerInput";
import moment from "moment";
import PermissionLoading from "../../components/PermissionLoading";
import WaveInput from "./components/WaveInput";
import RouteInput from "./components/RouteInput";
import AdhocOperatorTarget from "./components/AdhocOperatorTarget";

const AdhocOperatorEdit = (props: EditProps) => {
  const { id } = props;
  const { record, loading } = useEditController(props);

  const breadcrumb = [
    { link: "/adhocOperators", title: "Adhoc Operators" },
    { link: `/adhocOperators/${id}`, title: id },
  ];

  const formatRecord = (res: any) => {
    if (res) {
      if (res.driverId && res.busAssistantId) {
        res.target = "both";
      } else if (res.driverId) {
        res.target = "driver";
      } else if (res.busAssistantId) {
        res.target = "busAssistant";
      }

      return res;
    }
  };

  if (loading) {
    return <PermissionLoading />;
  }

  const transform = async (data: any) => {
    if (data.target === "driver") {
      data.busAssistantId = null;
    } else if (data.target === "busAssistant") {
      data.driverId = null;
    }

    return data;
  };

  return (
    <>
      <EditWrapper
        breadcrumb={breadcrumb}
        transform={transform}
        isDefaultTitle
        {...props}
      >
        <SimpleForm
          record={formatRecord(record)}
          toolbar={<CustomEditToolbar />}
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Route Plan</Typography>
            </Grid>
            <Grid item md={4} xs={12}>
              <WaveInput isDisable isEdit />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Route </Typography>
            </Grid>
            <Grid item md={4} xs={12}>
              <RouteInput isDisable />
            </Grid>
            <Grid item md={8} xs={12}></Grid>

            <Grid item xs={12} md={4}>
              <CustomDatePickerInput
                name="date"
                label="Select Replacement Date *"
                min={moment().add(1, "day").format("Y-MM-DD")}
                isWeekDays
              />
            </Grid>
            <Grid item xs={12} md={8}></Grid>

            <Grid item md={4} xs={12}>
              <AdhocOperatorTarget />
            </Grid>
            <Grid item md={8} xs={12}></Grid>

            <FormDataConsumer>
              {({ formData }) => {
                const showDriver =
                  formData.target === "driver" || formData.target === "both";
                const showBusAssistant =
                  formData.target === "busAssistant" ||
                  formData.target === "both";

                return (
                  <>
                    {showDriver && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="h6">Driver</Typography>
                        </Grid>
                        <Grid item md={4} xs={12}>
                          <ReferenceInput
                            reference="drivers"
                            source="driverId"
                            validate={required()}
                            filterToQuery={(searchText) => {
                              if (searchText !== "")
                                return { search: searchText };
                            }}
                          >
                            <AutocompleteInput
                              optionText={(choice: any) =>
                                `${choice?.fullName} (${choice?.contactNumber})`
                              }
                            />
                          </ReferenceInput>
                        </Grid>
                      </>
                    )}

                    {showBusAssistant && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="h6">Bus Assistant</Typography>
                        </Grid>
                        <Grid item md={4} xs={12}>
                          <ReferenceInput
                            reference="busAssistants"
                            source="busAssistantId"
                            validate={required()}
                            filterToQuery={(searchText) => {
                              if (searchText !== "")
                                return { search: searchText };
                            }}
                          >
                            <AutocompleteInput
                              optionText={(choice: any) =>
                                `${choice?.fullName} (${choice?.contactNumber})`
                              }
                            />
                          </ReferenceInput>
                        </Grid>
                      </>
                    )}

                    {(showDriver || showBusAssistant) && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="h6">Subcontractor</Typography>
                        </Grid>
                        <Grid item md={4} xs={12}>
                          <ReferenceInput
                            reference="subcontractors"
                            source="subcontractorId"
                            validate={required()}
                            filterToQuery={(searchText) => {
                              if (searchText !== "")
                                return { search: searchText };
                            }}
                          >
                            <AutocompleteInput
                              optionText={(choice: any) =>
                                `${choice?.name} (${choice?.subcontractorIdentifier ?? "N/A"})`
                              }
                            />
                          </ReferenceInput>
                        </Grid>
                      </>
                    )}
                  </>
                );
              }}
            </FormDataConsumer>

            <Grid item xs={12}></Grid>
            <Grid item xs={12} md={4}>
              <TextInput
                label="Remark"
                source="remark"
                multiline
                rows={3}
                validate={required()}
              />
            </Grid>
          </SanitizedGrid>
        </SimpleForm>
      </EditWrapper>
    </>
  );
};

export default AdhocOperatorEdit;
